import { useEffect, useRef, useState, useCallback } from 'react'
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
  type SimulationNodeDatum,
  type SimulationLinkDatum,
} from 'd3-force'
import { Skeleton } from '@/components/ui/skeleton'

interface GraphNode extends SimulationNodeDatum {
  id: number
  url: string
  label: string
  score: number | null
  depth: number
  inlinks: number
  title: string | null
}

interface GraphEdge extends SimulationLinkDatum<GraphNode> {
  source: number | GraphNode
  target: number | GraphNode
}

interface GraphData {
  nodes: GraphNode[]
  edges: GraphEdge[]
}

function scoreColor(score: number | null, isDark: boolean): string {
  if (score === null) return isDark ? '#6b7280' : '#9ca3af'
  if (score >= 80) return '#16a34a'
  if (score >= 50) return '#ca8a04'
  return '#dc2626'
}

function nodeRadius(n: GraphNode): number {
  return Math.max(4, Math.min(14, 3 + Math.sqrt(n.inlinks)))
}

export function StructureGraph({ crawlId }: { crawlId: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<GraphData | null>(null)
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null)
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(`/api/crawls/${crawlId}/links/graph`)
      .then((r) => r.json())
      .then((d) => {
        const graphData = d as GraphData
        setData(graphData)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [crawlId])

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark')

  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number, nodes: GraphNode[], edges: GraphEdge[]) => {
      ctx.clearRect(0, 0, width, height)

      // Draw edges
      ctx.strokeStyle = isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.12)'
      ctx.lineWidth = 0.5
      for (const edge of edges) {
        const source = edge.source as GraphNode
        const target = edge.target as GraphNode
        if (source.x != null && source.y != null && target.x != null && target.y != null) {
          ctx.beginPath()
          ctx.moveTo(source.x, source.y)
          ctx.lineTo(target.x, target.y)
          ctx.stroke()
        }
      }

      // Draw nodes
      for (const node of nodes) {
        if (node.x == null || node.y == null) continue
        const r = nodeRadius(node)
        ctx.beginPath()
        ctx.arc(node.x, node.y, r, 0, 2 * Math.PI)
        ctx.fillStyle = scoreColor(node.score, isDark)
        ctx.fill()
        ctx.strokeStyle = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'
        ctx.lineWidth = 0.5
        ctx.stroke()
      }
    },
    [isDark]
  )

  useEffect(() => {
    if (!data || !canvasRef.current || !containerRef.current) return

    const canvas = canvasRef.current
    const container = containerRef.current
    const ctx = canvas.getContext('2d')!

    // Set canvas to container width
    const rect = container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const width = rect.width
    const height = 500
    canvas.width = width * dpr
    canvas.height = height * dpr
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    ctx.scale(dpr, dpr)

    const nodes = data.nodes.map((n) => ({ ...n }))
    const edges = data.edges.map((e) => ({ ...e }))

    const simulation = forceSimulation<GraphNode>(nodes)
      .force(
        'link',
        forceLink<GraphNode, GraphEdge>(edges)
          .id((d) => d.id)
          .distance(50)
      )
      .force('charge', forceManyBody().strength(-100))
      .force('center', forceCenter(width / 2, height / 2))
      .force('collide', forceCollide<GraphNode>().radius((d) => nodeRadius(d) + 2))

    simulation.on('tick', () => {
      draw(ctx, width, height, nodes, edges)
    })

    // Mouse interaction for hover tooltips
    const handleMouseMove = (e: MouseEvent) => {
      const canvasRect = canvas.getBoundingClientRect()
      const x = e.clientX - canvasRect.left
      const y = e.clientY - canvasRect.top

      let found: GraphNode | null = null
      for (const node of nodes) {
        if (node.x == null || node.y == null) continue
        const dx = x - node.x
        const dy = y - node.y
        const r = nodeRadius(node)
        if (dx * dx + dy * dy < (r + 4) * (r + 4)) {
          found = node
          break
        }
      }
      setHoveredNode(found)
      if (found) {
        setTooltipPos({ x: e.clientX - canvasRect.left, y: e.clientY - canvasRect.top })
      }
    }

    canvas.addEventListener('mousemove', handleMouseMove)

    return () => {
      simulation.stop()
      canvas.removeEventListener('mousemove', handleMouseMove)
    }
  }, [data, draw])

  if (loading) return <Skeleton className="h-[500px] w-full" />
  if (!data || data.nodes.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No internal link data available for graph view.</p>
  }

  return (
    <div ref={containerRef} className="relative">
      <canvas ref={canvasRef} className="w-full rounded-lg bg-background border" />
      {hoveredNode && (
        <div
          className="absolute bg-popover border rounded-lg p-2 text-xs shadow-lg max-w-[300px] pointer-events-none z-10"
          style={{
            left: Math.min(tooltipPos.x + 12, (containerRef.current?.clientWidth ?? 400) - 200),
            top: Math.max(tooltipPos.y - 60, 8),
          }}
        >
          <p className="font-mono truncate">{hoveredNode.label}</p>
          {hoveredNode.title && (
            <p className="text-muted-foreground truncate">{hoveredNode.title}</p>
          )}
          <p className="mt-0.5">
            Score: {hoveredNode.score ?? 'N/A'} · Depth: {hoveredNode.depth} · Inlinks:{' '}
            {hoveredNode.inlinks}
          </p>
        </div>
      )}
      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Good (80+)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> OK (50-79)
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Poor (&lt;50)
        </span>
        <span className="ml-auto">Node size = inbound links · Showing top 200 pages</span>
      </div>
    </div>
  )
}
